<template>
  <div 
    :class="classes" 
    @mousedown="handleBackdropClick"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="titleId"
    @keydown="handleKeydown"
    ref="modal"
  >
    <div 
      class="box" 
      :style="style" 
      @mousedown.stop
      ref="modalContent"
    >
      <slot name="default"></slot>
    </div>
  </div>
</template>

<script>

export default {
  props: {
    fullscreen: {
      type: Boolean
    },
    width: {},
    disableScroll: {
      type: Boolean
    },
    titleId: {
      type: String,
      default: 'modal-title'
    }
  },
  data() {
    return {
      previousActiveElement: null,
      focusableElements: []
    }
  },
  computed: {
    classes () {
      return {
        'modal': true,
        'fullscreen': this.fullscreen,
        'disable-scroll': this.disableScroll
      }
    },
    style () {
      return this.width ? 'width: ' + this.width + 'px;' : ''
    }
  },
  methods: {
    handleBackdropClick() {
      this.$emit('done')
    },
    handleKeydown(event) {
      // Handle ESC key to close modal
      if (event.key === 'Escape') {
        event.preventDefault()
        this.$emit('done')
        return
      }
      
      // Handle Tab key for focus trapping
      if (event.key === 'Tab') {
        this.trapFocus(event)
      }
    },
    trapFocus(event) {
      if (this.focusableElements.length === 0) return
      
      const firstElement = this.focusableElements[0]
      const lastElement = this.focusableElements[this.focusableElements.length - 1]
      
      // Special handling for iframe elements
      const isIframeActive = document.activeElement && document.activeElement.tagName === 'IFRAME'
      const iframes = this.focusableElements.filter(el => el.tagName === 'IFRAME')
      
      if (event.shiftKey) {
        // Shift + Tab: going backwards
        if (document.activeElement === firstElement) {
          event.preventDefault()
          lastElement.focus()
        } else if (isIframeActive && iframes.length > 0) {
          // If we're in an iframe and going backwards, move to previous element
          const currentIframe = document.activeElement
          const iframeIndex = this.focusableElements.indexOf(currentIframe)
          if (iframeIndex > 0) {
            event.preventDefault()
            this.focusableElements[iframeIndex - 1].focus()
          } else {
            event.preventDefault()
            lastElement.focus()
          }
        }
      } else {
        // Tab: going forwards
        if (document.activeElement === lastElement) {
          event.preventDefault()
          firstElement.focus()
        } else if (isIframeActive && iframes.length > 0) {
          // If we're in an iframe and going forwards, move to next element
          const currentIframe = document.activeElement
          const iframeIndex = this.focusableElements.indexOf(currentIframe)
          if (iframeIndex < this.focusableElements.length - 1) {
            event.preventDefault()
            this.focusableElements[iframeIndex + 1].focus()
          } else {
            event.preventDefault()
            firstElement.focus()
          }
        }
      }
    },
    getFocusableElements() {
      const focusableSelectors = [
        'button',
        '[href]',
        'input',
        'select',
        'textarea',
        'iframe',
        '[tabindex]:not([tabindex="-1"])',
        '[role="button"]'
      ]
      
      const elements = this.$refs.modalContent.querySelectorAll(focusableSelectors.join(', '))
      this.focusableElements = Array.from(elements).filter(element => {
        return !element.disabled && 
               element.offsetWidth > 0 && 
               element.offsetHeight > 0 &&
               element.tabIndex !== -1
      })
    },
    setInitialFocus() {
      this.getFocusableElements()
      
      if (this.focusableElements.length > 0) {
        // Focus on the first focusable element
        this.focusableElements[0].focus()
      } else {
        // If no focusable elements, focus on the modal container
        this.$refs.modal.focus()
      }
    },
    restoreFocus() {
      if (this.previousActiveElement && this.previousActiveElement.isConnected) {
        // Ensure the element is still in the DOM and focusable
        try {
          this.previousActiveElement.focus()
        } catch (error) {
          // If focus fails, try to focus a nearby focusable element
          const fallbackElement = document.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
          if (fallbackElement) {
            fallbackElement.focus()
          }
        }
      }
    }
  },
  mounted() {
    // Store the currently focused element
    this.previousActiveElement = document.activeElement
    
    // Set initial focus after DOM is ready
    this.$nextTick(() => {
      this.setInitialFocus()
    })
    
    // Prevent background scrolling
    document.body.style.overflow = 'hidden'
  },
  beforeDestroy() {
    // Restore background scrolling
    document.body.style.overflow = ''
    
    // Restore focus to the original element
    this.$nextTick(() => {
      this.restoreFocus()
    })
  }
}

</script>

<style scoped lang="scss">

.modal {
  position: fixed;
  z-index: 100;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  padding: 0;
  overflow: hidden;
  
  /* Make modal focusable as fallback */
  &:focus {
    outline: none;
  }

  .box {
    background-color: white;
    width: min-content;
    min-width: 320px;
    height: auto;
    max-height: 100%;
    border-radius: 8px;
    overflow: auto;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
  }

  &.disable-scroll .box {
    overflow: hidden;
  }

  &.fullscreen .box {
    width: 100%;
    height: 100%;
    border-radius: 0;
    box-shadow: none;
  }
}

@media (max-width: 600px) {
  .modal .box {
    margin-left: 8px;
    margin-right: 8px;
    width: calc(100% - 16px);
  }
}

</style>